import React, { useState, useEffect } from 'react';

const IosInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  useEffect(() => {
    // Detect if user is on iOS / iPadOS Safari and not in standalone mode
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    const isDismissed = localStorage.getItem('ios_pwa_prompt_dismissed');

    if (isIos && !isStandalone && !isDismissed) {
      // Show prompt after a brief 2-second engagement
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('ios_pwa_prompt_dismissed', 'true');
  };

  if (!showPrompt && !showGuideModal) return null;

  return (
    <>
      {/* Floating iOS Install Banner */}
      {showPrompt && (
        <div
          style={{
            position: 'fixed',
            bottom: '72px',
            left: '16px',
            right: '16px',
            maxWidth: '480px',
            margin: '0 auto',
            background: 'linear-gradient(135deg, #18181B 0%, #27272A 100%)',
            color: '#FFFFFF',
            padding: '14px 16px',
            borderRadius: '16px',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                background: 'var(--clr-primary, #FF5C56)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(255, 92, 86, 0.4)',
              }}
            >
              📖
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
                Install Bible Contest App
              </div>
              <div style={{ fontSize: '12px', color: '#A1A1AA', marginTop: '3px' }}>
                Add to Home Screen for fast full-screen gameplay!
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowGuideModal(true)}
              style={{
                background: 'var(--clr-primary, #FF5C56)',
                color: '#ffffff',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              How to Install
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close install banner"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#71717A',
                fontSize: '18px',
                padding: '4px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* iOS Step-by-Step Installation Modal */}
      {showGuideModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setShowGuideModal(false)}
        >
          <div
            style={{
              background: 'var(--clr-surface, #FFFFFF)',
              color: 'var(--clr-text-1, #18181B)',
              width: '100%',
              maxWidth: '460px',
              borderRadius: '24px',
              padding: '24px',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'slideUp 0.25s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--clr-text-1)' }}>
                📲 Install on iPhone / iPad
              </h3>
              <button
                type="button"
                onClick={() => setShowGuideModal(false)}
                style={{
                  background: '#F4F4F5',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '13.5px', color: 'var(--clr-muted, #71717A)', marginTop: 0, marginBottom: '20px', lineHeight: 1.4 }}>
              Follow these simple steps in Safari to add Bible Contest to your iPhone or iPad home screen:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#FFF4F3', color: '#FF5C56', fontWeight: 800, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  1
                </div>
                <div style={{ fontSize: '13.5px' }}>
                  Tap the <strong>Share</strong> icon ( <span style={{ fontSize: '16px' }}>⎋</span> or square with arrow ) at the bottom of Safari.
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#FFF4F3', color: '#FF5C56', fontWeight: 800, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  2
                </div>
                <div style={{ fontSize: '13.5px' }}>
                  Scroll down the share sheet and tap <strong>"Add to Home Screen"</strong> ( <span style={{ fontSize: '16px' }}>➕</span> ).
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#FFF4F3', color: '#FF5C56', fontWeight: 800, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  3
                </div>
                <div style={{ fontSize: '13.5px' }}>
                  Tap <strong>Add</strong> in the top-right corner. Bible Contest is now on your home screen!
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowGuideModal(false);
                setShowPrompt(false);
                localStorage.setItem('ios_pwa_prompt_dismissed', 'true');
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--clr-primary, #FF5C56)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default IosInstallPrompt;
